/**
 * Task Panel
 *
 * Collapsible sidebar panel showing pending/scheduled schedulable tasks
 * with priority badges, deadline countdowns, and auto-schedule buttons.
 */

import { useState, useMemo } from 'react';
import { Clock, Zap, Check, Plus, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SchedulableTaskDetail } from '@/lib/api';

interface TaskPanelProps {
  tasks: SchedulableTaskDetail[];
  onNewTask: () => void;
  onAutoSchedule: (task: SchedulableTaskDetail) => void;
  onCompleteTask: (taskId: string) => void;
  onClickTask: (task: SchedulableTaskDetail) => void;
  taskSortOrder?: 'priority' | 'deadline' | 'created';
  showCompletedTasks?: 'never' | '24h' | '7d';
}

const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  medium: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  low: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
};

function getDeadlineLabel(deadline: string): string {
  const now = new Date();
  const dl = new Date(deadline);
  const diffMs = dl.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Overdue';
  if (diffDays === 0) return 'Due today';
  if (diffDays === 1) return 'Due tomorrow';
  if (diffDays <= 7) return `${diffDays}d left`;
  return `${Math.ceil(diffDays / 7)}w left`;
}

function getDeadlineColor(deadline: string): string {
  const now = new Date();
  const dl = new Date(deadline);
  const diffMs = dl.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'text-red-500';
  if (diffDays <= 1) return 'text-orange-500';
  return 'text-muted-foreground';
}

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

function sortTasks(
  tasks: SchedulableTaskDetail[],
  order: 'priority' | 'deadline' | 'created',
): SchedulableTaskDetail[] {
  return [...tasks].sort((a, b) => {
    switch (order) {
      case 'priority':
        return (PRIORITY_ORDER[a.priority] ?? 1) - (PRIORITY_ORDER[b.priority] ?? 1);
      case 'deadline': {
        if (!a.deadline && !b.deadline) return 0;
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;
        return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
      }
      case 'created':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      default:
        return 0;
    }
  });
}

export function TaskPanel({
  tasks,
  onNewTask,
  onAutoSchedule,
  onCompleteTask,
  onClickTask,
  taskSortOrder = 'priority',
  showCompletedTasks = 'never',
}: TaskPanelProps) {
  const [expandedSection, setExpandedSection] = useState<
    'pending' | 'scheduled' | 'completed' | null
  >('pending');

  const pendingTasks = useMemo(
    () =>
      sortTasks(
        tasks.filter((t) => t.status === 'pending'),
        taskSortOrder,
      ),
    [tasks, taskSortOrder],
  );
  const scheduledTasks = useMemo(
    () =>
      sortTasks(
        tasks.filter((t) => t.status === 'scheduled'),
        taskSortOrder,
      ),
    [tasks, taskSortOrder],
  );
  const completedTasks = useMemo(() => {
    if (showCompletedTasks === 'never') return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - (showCompletedTasks === '24h' ? 1 : 7));
    return tasks.filter((t) => t.status === 'completed' && new Date(t.createdAt) >= cutoff);
  }, [tasks, showCompletedTasks]);

  const renderTaskItem = (task: SchedulableTaskDetail) => (
    <div
      key={task.id}
      className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-muted/50 cursor-pointer group"
      onClick={() => onClickTask(task)}
    >
      {task.status === 'pending' ? (
        <button
          type="button"
          className="mt-0.5 h-4 w-4 rounded border border-muted-foreground/40 shrink-0 hover:border-primary"
          onClick={(e) => {
            e.stopPropagation();
            onCompleteTask(task.id);
          }}
          title="Mark complete"
        />
      ) : (
        <Check className="h-4 w-4 mt-0.5 text-green-500 shrink-0" />
      )}

      <div className="flex-1 min-w-0">
        <div className="text-sm truncate">{task.title}</div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${PRIORITY_COLORS[task.priority]}`}
          >
            {task.priority}
          </span>
          <span
            className={`text-[10px] flex items-center gap-0.5 ${getDeadlineColor(task.deadline)}`}
          >
            <Clock className="h-2.5 w-2.5" />
            {getDeadlineLabel(task.deadline)}
          </span>
          {task.estimatedMinutes && (
            <span className="text-[10px] text-muted-foreground">{task.estimatedMinutes}m</span>
          )}
        </div>
      </div>

      {task.status === 'pending' && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            onAutoSchedule(task);
          }}
          title="Auto-schedule"
        >
          <Zap className="h-3 w-3" />
        </Button>
      )}
    </div>
  );

  return (
    <div className="border-t pt-2">
      <div className="flex items-center justify-between px-3 mb-1">
        <span className="text-xs font-semibold uppercase text-muted-foreground">Tasks</span>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5"
          onClick={onNewTask}
          title="New task"
        >
          <Plus className="h-3 w-3" />
        </Button>
      </div>

      {/* Pending section */}
      <button
        type="button"
        className="flex items-center gap-1 px-3 py-1 text-xs text-muted-foreground hover:text-foreground w-full text-left"
        onClick={() => setExpandedSection(expandedSection === 'pending' ? null : 'pending')}
      >
        {expandedSection === 'pending' ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        Pending ({pendingTasks.length})
      </button>
      {expandedSection === 'pending' && (
        <div className="px-1 space-y-0.5">{pendingTasks.map(renderTaskItem)}</div>
      )}

      {/* Scheduled section */}
      {scheduledTasks.length > 0 && (
        <>
          <button
            type="button"
            className="flex items-center gap-1 px-3 py-1 text-xs text-muted-foreground hover:text-foreground w-full text-left"
            onClick={() => setExpandedSection(expandedSection === 'scheduled' ? null : 'scheduled')}
          >
            {expandedSection === 'scheduled' ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            Scheduled ({scheduledTasks.length})
          </button>
          {expandedSection === 'scheduled' && (
            <div className="px-1 space-y-0.5">{scheduledTasks.map(renderTaskItem)}</div>
          )}
        </>
      )}

      {/* Completed section */}
      {completedTasks.length > 0 && (
        <>
          <button
            type="button"
            className="flex items-center gap-1 px-3 py-1 text-xs text-muted-foreground hover:text-foreground w-full text-left"
            onClick={() => setExpandedSection(expandedSection === 'completed' ? null : 'completed')}
          >
            {expandedSection === 'completed' ? (
              <ChevronDown className="h-3 w-3" />
            ) : (
              <ChevronRight className="h-3 w-3" />
            )}
            Completed ({completedTasks.length})
          </button>
          {expandedSection === 'completed' && (
            <div className="px-1 space-y-0.5 opacity-60">{completedTasks.map(renderTaskItem)}</div>
          )}
        </>
      )}

      {tasks.length === 0 && (
        <p className="text-xs text-muted-foreground px-3 py-2">
          No tasks yet. Add one to get AI scheduling suggestions.
        </p>
      )}
    </div>
  );
}
